import { TreeNodeSourceType } from '../_generated/api/structure'
import type { Ancestor } from '../models/ApiStructureModel'
import DirectoryEntry from '../models/DirectoryEntry'

export const StructureTypes = { ...TreeNodeSourceType, None: 'none', Bolt: 'bolt' } as const
export type StructureType = (typeof StructureTypes)[keyof typeof StructureTypes]

export default class Structure {
    constructor(private structure: DirectoryEntry[]) {}

    get rootCount() {
        return this.structure.length
    }

    get empty() {
        return this.rootCount <= 0
    }

    map<T>(fn: (entry: DirectoryEntry, i: number) => T) {
        return this.structure.map(fn)
    }

    findRoot(root: DirectoryEntry | string) {
        const rootId = typeof root === 'string' ? root : root.id
        return this.structure.find((root) => root.id === rootId)
    }

    findEntry(targetId: string) {
        const path = this.findEntryPath(targetId)
        return path?.at(-1)
    }

    findEntryAncestors(targetId: string): Ancestor[] {
        // Use the loaded tree so callers with only an id receive links built from actual node types.
        // Entity screens use the API-provided ancestor projection through ApiStructureModel instead.
        const path = this.findEntryPath(targetId)
        if (!path) return []
        return path.map((entry: DirectoryEntry) => ({
            id: entry.id,
            name: entry.name,
            link: entry.referenceLink,
        }))
    }

    /*
     * Find the path to any target from its respective root.
     * NOTE: If you already know the root, consider using findEntryPathFromRoot instead to save performance.
     */
    findEntryPath(targetId: string) {
        for (const root of this.structure) {
            const path = this.findEntryPathFromRoot(root, targetId)
            if (path) return path
        }
    }

    findEntryPathFromRoot(
        root: DirectoryEntry | string,
        target: DirectoryEntry | string
    ): DirectoryEntry[] | undefined {
        const rootEntry = this.findRoot(root)
        if (!rootEntry) return undefined
        const targetId = typeof target === 'string' ? target : target.id

        // Employ recursive traversal to find the path from any root to any entry, given both exist.
        const buildEntryPath = (entry: DirectoryEntry): DirectoryEntry[] | undefined => {
            if (entry.id === targetId) return [entry]
            for (const child of entry.children) {
                const next = buildEntryPath(child)
                if (next) return [entry, ...next]
            }
            return undefined
        }

        return buildEntryPath(rootEntry)
    }
}
